// Story 6.16 — per-user ledger export at /admin/ledger-export.
// Wrapped by <AdminGuard> in App.tsx. Desktop-only.
//
// Pick a user + a date range and download a complete, ordered, reproducible CSV
// of every money record touching them in that window — the statement you hand an
// accountant or an investigator. The platform-wide variant (no user) is the same
// columns for the whole platform, which the period-close screen links into.

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@greenhold/components";

import { downloadLedgerExport } from "../features/admin/useLedgerExport";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s));
}

export function AdminLedgerExportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userId, setUserId] = useState(searchParams.get("user") ?? "");
  const [from, setFrom] = useState(searchParams.get("from") ?? "");
  const [to, setTo] = useState(searchParams.get("to") ?? "");
  const [semicolon, setSemicolon] = useState(false);
  const [busy, setBusy] = useState(false);

  function rangeValid(): boolean {
    if (!isDate(from) || !isDate(to)) {
      toast.error("Enter both dates as YYYY-MM-DD");
      return false;
    }
    if (from > to) {
      toast.error("`from` must be on or before `to`");
      return false;
    }
    return true;
  }

  async function download(scopeKind: "user" | "platform") {
    if (!rangeValid()) return;
    if (scopeKind === "user" && !UUID_RE.test(userId.trim())) {
      toast.error("Enter a valid user UUID");
      return;
    }
    // Persist the lookup in the URL so a report is reproducible from a link.
    const next = new URLSearchParams(searchParams);
    next.set("from", from);
    next.set("to", to);
    if (scopeKind === "user") next.set("user", userId.trim());
    setSearchParams(next, { replace: true });

    setBusy(true);
    try {
      const { rowCount } = await downloadLedgerExport({
        scope:
          scopeKind === "user"
            ? { kind: "user", userId: userId.trim() }
            : { kind: "platform" },
        from,
        to,
        delimiter: semicolon ? "semicolon" : "comma",
      });
      toast.success(
        rowCount === null ? "Export downloaded" : `Export downloaded — ${rowCount} rows`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-ledger-export-page">
      <div>
        <h1 className="text-2xl font-semibold">Ledger export</h1>
        <p className="text-sm text-gray-500 mt-1">
          A complete, ordered, reproducible CSV of every money record touching a user in a date
          range — a statement, not a spreadsheet. Exporting is audited.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Range</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <label className="flex-1 text-sm">
              From (inclusive, UTC)
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded font-mono text-sm"
                aria-label="from date"
              />
            </label>
            <label className="flex-1 text-sm">
              To (inclusive, UTC)
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded font-mono text-sm"
                aria-label="to date"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={semicolon}
              onChange={(e) => setSemicolon(e.target.checked)}
            />
            Semicolon-separated (Danish Excel defaults to <code>;</code> — otherwise the file is
            UTF-8 + BOM, RFC 4180 comma)
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>One user</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="user UUID (e.g. 9f4c2a8e-1234-...)"
            className="w-full px-3 py-2 border rounded font-mono text-sm"
            aria-label="user UUID"
          />
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => download("user")}
            data-testid="download-user-export"
          >
            Download user CSV
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Platform-wide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-500">
            Every movement in the window, same columns. Used to justify a period-close figure line
            by line.
          </p>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => download("platform")}
            data-testid="download-platform-export"
          >
            Download platform-wide CSV
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminLedgerExportPage;
